import { Injectable } from '@angular/core';
import { AgGridColumns, AgGridFilterParams } from '../../../Compliance/AgGrid';
import {
    CellClickedEvent,
    ColDef,
    GridApi,
    IRowNode,
    ValueFormatterParams,
    ValueGetterParams,
} from 'ag-grid-community';
import {
    AgGridLinkCellRendererComponent,
    AgGridLinkCellRendererParams,
} from '../../../Compliance/AgGrid/CellRenderers/agGridLinkCellRenderer.component';
import { AgGridYesNoFloatingFilterComponent } from '../../../Compliance/AgGrid/FloatingFilters/agGridYesNoFloatingFilter.component';
import { AgGridMultiSelectTracker } from '../../../Compliance/AgGrid/MultiSelectTracker';
import { NubService } from '../../../Navigation/Nub/nub.service';
import { AdvancedSearchPersistenceService } from '../advancedSearchPersistence.service';
import { SmartSearchService } from '../smartSearch.service';
import { CommentsService } from '../../../Comments/comments.service';
import { WeissmanDateFormat } from '../../../UI-Lib/Pipes/Date-Format/date-formatting.pipe';
import { ContactModalOrigin, EntityTypeIds } from '../../../constants.new';
import { PopUpService } from '../../../Common/Popup/popup.service';
import { CommentsModalService } from '../../../Comments/commentsModal.service';
import moment from 'moment-timezone';
import { AttachmentService } from 'src/app/Attachment/attachment.service';
import { DocumentService } from 'src/app/Documents/document.service';
import { ContactModalService } from '../../../Contact/contactModal.service';
import AdvancedSearchField = Core.AdvancedSearchField;
import { AnchorSREF } from '../../../UI-Lib/Directives/Anchor-Route/anchorRoute.directive';

@Injectable()
export class AdvancedSearchColumnBuilder {
    constructor(
        private readonly _advancedSearchPersistenceService: AdvancedSearchPersistenceService,
        private readonly _smartSearchService: SmartSearchService,
        private readonly _commentsService: CommentsService,
        private readonly _commentsModalService: CommentsModalService,
        private readonly _documentService: DocumentService,
        private readonly _attachmentService: AttachmentService,
        private readonly _contactModalService: ContactModalService,
        private readonly _popUpService: PopUpService,
        private readonly _nubService: NubService,
    ) {}

    getColumnDefinition(
        columnId: string,
        field: Core.AdvancedSearchField,
        gridApi: GridApi,
        gridTracker: AgGridMultiSelectTracker,
        source: string = 'smart',
    ): ColDef {
        // Setup common column properties used by all column types
        // Depending on the column the cell renderer could get replaced
        let baseDefinition: any = {
            colId: columnId,
            headerName: field.resultsColumnName,
            field: columnId,
            hide: !field.visible,
        };

        // If formatting is provided by the back end, apply it
        const suppliedFormatting = {};
        if (field.advancedSearchDataType && field.advancedSearchDataType.formatting) {
            const dataTypeCustomFormatting = JSON.parse(field.advancedSearchDataType.formatting);
            Object.keys(dataTypeCustomFormatting).forEach((key: string) => {
                suppliedFormatting[key] = dataTypeCustomFormatting[key];
            });
        }

        // Get and apply the type based column settings
        baseDefinition = { ...baseDefinition, ...this._getTypeColumnProperties(field, columnId), ...suppliedFormatting };

        // Wrap valueFormatter with prefix/postfix if provided by the data type
        const prefix = field.advancedSearchDataType?.prefix || '';
        const postfix = field.advancedSearchDataType?.postfix || '';
        if ((prefix || postfix) && baseDefinition.valueFormatter) {
            const originalFormatter = baseDefinition.valueFormatter;
            baseDefinition.valueFormatter = (params: ValueFormatterParams) => {
                const formatted = originalFormatter(params);
                return formatted !== '' ? `${prefix}${formatted}${postfix}` : '';
            };
        }

        // Check to see if a field specific click event is needed
        const clickHandler = this._fieldSpecificClickEvents(field);
        if (clickHandler) {
            baseDefinition.onCellClicked = (params: CellClickedEvent) => {
                const rowData = params.data;
                clickHandler(rowData);
            };
        }

        // Set renderer if comment type
        switch (field.advancedSearchFieldID) {
            case 9007: // Document Intake Number of Comments
            case 1916: // Invoice Number of Comments
            case 1444: // Payment Number of Comments
            case 1044: // Revision Number of Comments
            case 1052: // Assessment DRTT Number of Comments
            case 1456: // Tax Bill DRTT Number of Comments
                baseDefinition = { ...baseDefinition, ...this._getCommentCell() };
                break;
            case 601: // Parcel Acct Num
                baseDefinition = { ...baseDefinition, filterParams: AgGridFilterParams.textFilterWithBlankOptionsAndCommaParams };
                break;
        }

        // If it is a navigation field and there is no other renderer set, provide the correct renderer and nav link
        if (!baseDefinition.cellRenderer && field.navigation) {
            switch (field.navigation) {
                case 'CompanyContactID':
                case 'SiteContactID':
                case 'ParcelContactID':
                    // Contact doesn't route but needs to open modal
                    baseDefinition.cellRenderer = AgGridLinkCellRendererComponent;
                    baseDefinition.cellRendererParams = {
                        newWindow: false,
                        getButtonLink: (params: AgGridLinkCellRendererParams) => params.value,
                        onClickAsync: async (params: AgGridLinkCellRendererParams) => {
                            const fieldValue = this._getNavigationalFieldValueByName(params.data, field.navigation);
                            await this._contactModalService.openContactDialog(
                                fieldValue,
                                ContactModalOrigin.EntityContact,
                            );
                        },
                    } as AgGridLinkCellRendererParams;
                    break;
                default:
                    const navigationRoute = this._getNavigationRoute(field.navigation, columnId);
                    baseDefinition.cellRenderer = AgGridLinkCellRendererComponent;
                    baseDefinition.cellRendererParams = {
                        newWindow: false,
                        isHelpDisabled: true,
                        isDisabled: (params) => !navigationRoute,
                        getSref: (params: AgGridLinkCellRendererParams) => {
                            const entry = params.data;
                            if (!entry || !navigationRoute) {
                                return null;
                            }
                            return navigationRoute(entry);
                        },
                        onClick: (params: AgGridLinkCellRendererParams) => {
                            this._setPersistentState(params.node, source, gridApi, gridTracker);
                        },
                    } as AgGridLinkCellRendererParams;
            }
        }

        return baseDefinition;
    }

    private _getNavigationRoute(navigationFieldName: string, columnId: string): (rowData) => AnchorSREF {
        switch (navigationFieldName) {
            case 'CompanyID':
            case 'Company_ParentCompanyID':
                return (rowData) => {
                    return {
                        target: 'company',
                        options: {
                            companyId: this._getNavigationalFieldValueByName(rowData, navigationFieldName),
                        },
                    };
                };

            case 'SiteID':
            case 'Master SiteID':
                return (rowData) => {
                    const fieldValue = this._getNavigationalFieldValueByName(rowData, navigationFieldName);

                    if (!fieldValue) {
                        //this._toastr.warning('Site output column must be included for navigation.');
                        return null;
                    }
                    return {
                        target: 'siteRedirect',
                        options: {
                            entityId: fieldValue,
                        },
                    };
                };

            case 'ParcelID':
            case 'Master ParcelID':
            case 'PP Reporting ParcelID':
                return (rowData) => {
                    const fieldValue = this._getNavigationalFieldValueByName(rowData, navigationFieldName);

                    if (!fieldValue) {
                        //this._toastr.warning('Parcel output column must be included for navigation.');
                        return null;
                    }
                    return {
                        target: 'parcelRedirect',
                        options: {
                            entityId: fieldValue,
                        },
                    };
                };

            case 'AssessorID':
            case 'LitigationReferral AssessorID':
                return (rowData) => {
                    const fieldValue = this._getNavigationalFieldValueByName(rowData, navigationFieldName);

                    return {
                        target: 'assessorRedirect',
                        options: {
                            entityId: fieldValue,
                        },
                    };
                };

            case 'CollectorID':
                return (rowData) => {
                    const fieldValue = this._getNavigationalFieldValueByName(rowData, navigationFieldName);
                    return {
                        target: 'collectorRedirect',
                        options: {
                            entityId: fieldValue,
                        },
                    };
                };

            case 'StateID':
                return (rowData) => {
                    return {
                        target: 'state',
                        options: {
                            stateId: this._getNavigationalFieldValueByName(rowData, navigationFieldName),
                        },
                    };
                };

            case 'AnnualYearID':
                return (rowData) => {
                    const fieldValue = this._getNavigationalFieldValueByName(rowData, navigationFieldName);
                    return {
                        target: 'annualYearRedirect',
                        options: {
                            entityId: fieldValue,
                        },
                    };
                };

            case 'AnnualAssessmentID':
                return (rowData) => {
                    const fieldValue = this._getNavigationalFieldValueByName(rowData, navigationFieldName);
                    return {
                        target: 'annualAssessmentRedirect',
                        options: {
                            entityId: fieldValue,
                        },
                    };
                };

            case 'AppealID':
                return (rowData) => {
                    const fieldValue = this._getNavigationalFieldValueByName(rowData, navigationFieldName);
                    return {
                        target: 'appealRedirect',
                        options: {
                            entityId: fieldValue,
                        },
                    };
                };

            case 'FilingID':
                return (rowData) => {
                    let fieldName = 'Filing';
                    let fieldValue = this._getNavigationalFieldValueByName(rowData, navigationFieldName);

                    if (!fieldValue) {
                        fieldValue = this._getNavigationalFieldValueByName(rowData, 'FilingBatchID');
                        fieldName = 'FilingBatch';
                    }

                    const state = fieldName === 'Filing' ? 'parcelFilingRedirect' : 'filingBatchRedirect';

                    return {
                        target: state,
                        options: {
                            entityId: fieldValue,
                        },
                    };
                };

            case 'FilingBatchID':
                return (rowData) => {
                    const fieldValue = this._getNavigationalFieldValueByName(rowData, navigationFieldName);
                    return {
                        target: 'filingBatchRedirect',
                        options: {
                            entityId: fieldValue,
                        },
                    };
                };

            case 'RefundID':
                return (rowData) => {
                    const fieldValue = this._getNavigationalFieldValueByName(rowData, navigationFieldName);
                    return {
                        target: 'refundRedirect',
                        options: {
                            entityId: fieldValue,
                        },
                    };
                };

            case 'BillClusterID':
                return (rowData) => {
                    const fieldValue = this._getNavigationalFieldValueByName(rowData, navigationFieldName);
                    return {
                        target: 'billClusterRedirect',
                        options: {
                            entityId: fieldValue,
                        },
                    };
                };

            case 'PaymentID':
                return (rowData) => {
                    return {
                        target: 'paymentRedirect',
                        options: {
                            entityId: this._getNavigationalFieldValueByName(rowData, navigationFieldName),
                        },
                    };
                };

            case 'InvoiceID':
                return (rowData) => {
                    const fieldValue = this._getNavigationalFieldValueByName(rowData, navigationFieldName);
                    return {
                        target: 'invoiceRedirect',
                        options: {
                            entityId: fieldValue,
                        },
                    };
                };

            case 'LitigationReferralID':
            case 'Appeal Litigation Referral ID':
                return (rowData) => {
                    const fieldValue = this._getNavigationalFieldValueByName(rowData, navigationFieldName);
                    return {
                        target: 'litigationReferal',
                        options: {
                            referralId: fieldValue,
                        },
                    };
                };

            default:
                console.log('advanced search - unimplemented navigation - ', navigationFieldName);
                return null;
        }
    }

    private _getTypeColumnProperties(field: Core.AdvancedSearchField, columnId: string): ColDef {
        const minimumFractionDigits = field.advancedSearchDataType.precision || 0;
        switch (field.advancedSearchDataTypeID) {
            case Core.AdvancedSearchDataTypeEnum.money:
            case Core.AdvancedSearchDataTypeEnum.Currency_0:
            case Core.AdvancedSearchDataTypeEnum.Currency_2:
                return {
                    width: AgGridColumns.numericColumnWidth,
                    filter: 'agNumberColumnFilter',
                    filterParams: AgGridFilterParams.numberWithRangeAndEqualToAndBlankFilterParams,
                    floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
                    valueFormatter: (params: ValueFormatterParams) => {
                        const valueToFormat = params.value;

                        if (typeof valueToFormat !== 'number') {
                            return '';
                        }

                        const formatter = new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            minimumFractionDigits: minimumFractionDigits,
                            maximumFractionDigits: minimumFractionDigits,
                        });

                        return formatter.format(valueToFormat);
                    },
                };
            case Core.AdvancedSearchDataTypeEnum.Date:
            case Core.AdvancedSearchDataTypeEnum.DateTime:
            case Core.AdvancedSearchDataTypeEnum.datetime2:
            case Core.AdvancedSearchDataTypeEnum.UTCDate:
            case Core.AdvancedSearchDataTypeEnum.UTCDateTime:
            case Core.AdvancedSearchDataTypeEnum.DateText:
                let dateFormatter;

                // For all other date types, the time coming from the server has already been
                // converted and is ready for display; we use UTC for transmission between the
                // server and client so that JavaScript doesn't convert the time to a local time.
                // So we use .utc() to retrieve the raw unprocessed date/time the server had
                // before transmission (see Utilities.Service.js convertDateStringsToDates
                // function and related functions for more info)
                if (field.advancedSearchDataType.name.indexOf('Date') >= 0) {
                    dateFormatter = (inputDateTime) => {
                        return moment(inputDateTime).utc().format('M/DD/YYYY');
                    };
                }

                if (field.advancedSearchDataType.name.indexOf('DateTime') >= 0) {
                    dateFormatter = (inputDateTime) => {
                        return moment(inputDateTime).utc().format('M/DD/YYYY h:mm A');
                    };
                }

                // For UTCDateTime and UTCDate type fields, the time coming from the server is
                // really a moment in time specified in UTC, and we need to convert it to Central
                // time before displaying
                if (field.advancedSearchDataType.name.indexOf('UTCDate') >= 0) {
                    dateFormatter = (inputDateTime) => {
                        return moment(inputDateTime).tz('America/Chicago').format('M/DD/YYYY');
                    };
                }

                if (field.advancedSearchDataType.name.indexOf('UTCDateTime') >= 0) {
                    dateFormatter = (inputDateTime) => {
                        return moment(inputDateTime).tz('America/Chicago').format('M/DD/YYYY h:mm A');
                    };
                }

                // UTCDate/UTCDateTime fields are displayed in Central Time (see dateFormatter above),
                // so the filter's calendar-day comparator must also use Central Time or the filtered
                // day won't match what the user sees on screen (see #585056).
                const isUtcDateField = field.advancedSearchDataType.name.indexOf('UTCDate') >= 0;

                return {
                    width: AgGridColumns.dateColumnWidth,
                    filter: 'agDateColumnFilter',
                    filterParams: isUtcDateField
                        ? AgGridFilterParams.dateFilterParamsWithBlankOptionsCentralTimeParams
                        : AgGridFilterParams.dateFilterParamsWithBlankOptionsParams,
                    floatingFilterComponentParams: AgGridFilterParams.dateFloatingFilterParams,
                    valueGetter: (params: ValueGetterParams) => {
                        const valueToFormat = params.data[params.colDef.field];

                        if (!valueToFormat) {
                            return '';
                        }

                        if (valueToFormat === 'N/A') {
                            return valueToFormat;
                        }

                        return dateFormatter ? dateFormatter(valueToFormat) : WeissmanDateFormat(valueToFormat);
                    },
                    comparator: (valueA, valueB, nodeA, nodeB) => {
                        // Compare the raw underlying value (with full precision) rather than valueA/valueB,
                        // which are the already-formatted display strings from valueGetter above. Those
                        // strings are truncated to minute precision (no seconds) for DateTime/UTCDateTime
                        // fields, so any two rows within the same minute compared as equal and could sort
                        // in a non-chronological/unstable order. See #585964.
                        const rawA = nodeA?.data?.[columnId];
                        const rawB = nodeB?.data?.[columnId];

                        if (rawA == rawB) {
                            return 0;
                        }

                        if (rawA == null && rawB == null) {
                            return 0;
                        }

                        const cA = this._constructDateIfAbsent(rawA);
                        const cB = this._constructDateIfAbsent(rawB);

                        let result = 0;
                        if (cA > cB) {
                            result = 1;
                        }

                        if (cA < cB) {
                            result = -1;
                        }

                        if (cA == cB) {
                            result = 0;
                        }

                        return result;
                    },
                };
            case Core.AdvancedSearchDataTypeEnum.Percent_0:
            case Core.AdvancedSearchDataTypeEnum.Percent_1:
            case Core.AdvancedSearchDataTypeEnum.Percent_2:
            case Core.AdvancedSearchDataTypeEnum.Percent_3:
            case Core.AdvancedSearchDataTypeEnum.Percent_4:
            case Core.AdvancedSearchDataTypeEnum.Percent_5:
            case Core.AdvancedSearchDataTypeEnum.Percent_6:
                return {
                    width: AgGridColumns.numericColumnWidth,
                    filter: 'agNumberColumnFilter',
                    filterParams: AgGridFilterParams.numberWithRangeAndEqualToAndBlankFilterParams,
                    floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
                    valueFormatter: (params: ValueFormatterParams) => {
                        const valueToFormat = params.value;

                        if (!valueToFormat) {
                            return '';
                        }

                        const formatter = new Intl.NumberFormat('en-US', {
                            style: 'percent',
                            minimumFractionDigits: minimumFractionDigits,
                            maximumFractionDigits: minimumFractionDigits,
                        });

                        return formatter.format(valueToFormat);
                    },
                };
            case Core.AdvancedSearchDataTypeEnum.intType:
            case Core.AdvancedSearchDataTypeEnum.smallint:
            case Core.AdvancedSearchDataTypeEnum.Number_0:
            case Core.AdvancedSearchDataTypeEnum.Number_0_NoGrouping:
                return {
                    width: AgGridColumns.numericColumnWidth,
                    filter: 'agNumberColumnFilter',
                    filterParams: AgGridFilterParams.numberWithRangeAndEqualToAndBlankAndCommaFilterParams,
                    floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
                    valueFormatter: (params: ValueFormatterParams): string => {
                        const valueToFormat = params.value;

                        if (field.customFormatting.gridShowValueAsZeroIfZero && valueToFormat == 0) {
                            return '0';
                        }
                        if (field.customFormatting.gridShowValueAsZeroIfNull && valueToFormat == null) {
                            return '0';
                        }

                        if (typeof valueToFormat !== 'number') {
                            return '';
                        }

                        const formatter = new Intl.NumberFormat('en-US', {
                            style: 'decimal',
                            minimumFractionDigits: minimumFractionDigits,
                            maximumFractionDigits: minimumFractionDigits,
                            useGrouping: field.advancedSearchDataType.name.indexOf('NoGrouping') < 0,
                        });

                        return formatter ? formatter.format(valueToFormat) : `${valueToFormat}`;
                    },
                };
            case Core.AdvancedSearchDataTypeEnum.Number_1:
            case Core.AdvancedSearchDataTypeEnum.Number_2:
            case Core.AdvancedSearchDataTypeEnum.Number_3:
            case Core.AdvancedSearchDataTypeEnum.Number_7:
            case Core.AdvancedSearchDataTypeEnum.Number_4:
            case Core.AdvancedSearchDataTypeEnum.bit:
            case Core.AdvancedSearchDataTypeEnum.decimalType:
            case Core.AdvancedSearchDataTypeEnum.Year:
            case Core.AdvancedSearchDataTypeEnum.Number_0_Percent:                                
            case Core.AdvancedSearchDataTypeEnum.Number_1_Percent:
            case Core.AdvancedSearchDataTypeEnum.Number_2_Percent:
            case Core.AdvancedSearchDataTypeEnum.Number_3_Percent:
            case Core.AdvancedSearchDataTypeEnum.Number_4_Percent:
            case Core.AdvancedSearchDataTypeEnum.Number_5_Percent:
            case Core.AdvancedSearchDataTypeEnum.Number_6_Percent:
                return {
                    width: AgGridColumns.numericColumnWidth,
                    filter: 'agNumberColumnFilter',
                    filterParams: AgGridFilterParams.numberWithRangeAndEqualToAndBlankFilterParams,
                    floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
                    valueFormatter: (params: ValueFormatterParams): string => {
                        const valueToFormat = params.value;

                        if (field.customFormatting.gridShowValueAsZeroIfZero && valueToFormat == 0) {
                            return '0';
                        }
                        if (field.customFormatting.gridShowValueAsZeroIfNull && valueToFormat == null) {
                            return '0';
                        }

                        if (typeof valueToFormat !== 'number') {
                            return '';
                        }

                        const formatter = new Intl.NumberFormat('en-US', {
                            style: 'decimal',
                            minimumFractionDigits: minimumFractionDigits,
                            maximumFractionDigits: minimumFractionDigits,
                            useGrouping: field.advancedSearchDataType.name.indexOf('NoGrouping') < 0,
                        });

                        return formatter ? formatter.format(valueToFormat) : `${valueToFormat}`;
                    },
                };
            case Core.AdvancedSearchDataTypeEnum.picklist:
                return {
                    width: AgGridColumns.selectionColumnWidth,
                    filter: 'agTextColumnFilter',
                    filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
                    floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                };
            case Core.AdvancedSearchDataTypeEnum.Text_Align_Left:
            case Core.AdvancedSearchDataTypeEnum.Text_Align_Right:
            case Core.AdvancedSearchDataTypeEnum.Text_Align_Center:
            case Core.AdvancedSearchDataTypeEnum.varchar:
                let textAlign = 'left';
                if (field.advancedSearchDataTypeID === Core.AdvancedSearchDataTypeEnum.Text_Align_Right) {
                    textAlign = 'right';
                } else if (field.advancedSearchDataTypeID === Core.AdvancedSearchDataTypeEnum.Text_Align_Center) {
                    textAlign = 'center';
                }
                return {
                    width: AgGridColumns.textColumnWidth,
                    filter: 'agTextColumnFilter',
                    filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
                    floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                    cellStyle: {
                        textAlign: textAlign,
                    },
                };
            case Core.AdvancedSearchDataTypeEnum.YesNo:
            case Core.AdvancedSearchDataTypeEnum.YesNoNullable:
            case Core.AdvancedSearchDataTypeEnum.YesNoEmpty:
                return {
                    width: AgGridColumns.checkboxColumnMinWidth,
                    filter: 'agTextColumnFilter',
                    filterParams: AgGridFilterParams.yesNoFilterWithBlankOptionsParams,
                    floatingFilterComponent: AgGridYesNoFloatingFilterComponent,
                    floatingFilterComponentParams: AgGridFilterParams.booleanFloatingFilterParams,
                    valueFormatter: (params) => {
                        if (params.value || params.value === false) {
                            return params.value && !(params.value === 'no' || params.value === 'No') ? 'Yes' : 'No';
                        } else {
                            return '';
                        }
                    },
                };
            case Core.AdvancedSearchDataTypeEnum.AttachmentIconOpenPreview:
                return {
                    width: AgGridColumns.checkboxColumnMinWidth,
                    filter: 'agTextColumnFilter',
                    filterParams: AgGridFilterParams.booleanFilterWithBlankOptionsParams,
                    floatingFilterComponentParams: AgGridFilterParams.booleanFloatingFilterParams,
                    cellRenderer: (params) => {
                        if (params.value) {
                            if (params.colDef.pdfLoading && params.colDef.pdfLoading === params.value) {
                                return `<div class="text-center" style="margin-top: 0;">
                                            <i class="fa fa-spinner fa-pulse fa-1x"></i>
                                        </div>`;
                            } else {
                                return '<i class="fa fa-file-text-o" aria-hidden="true"></i>';
                            }
                        }
                        return '';
                    },
                    onCellClicked: async (params: CellClickedEvent) => {
                        // Anything using square bracket notation to access or assign a value is not supported by the
                        // Typing assigned to it, although it was part of the old JS code so I'm leaving it in place

                        const rowData = params.data;

                        params.colDef['pdfLoading'] = params.value;
                        params.api.redrawRows();

                        const attachmentId = rowData[`f${field.advancedSearchFieldID.toString()}`];

                        const response = await this._documentService.getDocumentBody(attachmentId);
                        const fileName = this._attachmentService.getFileNameFromHttpResponse(response);
                        const fileExtension = fileName.split('.')[1].substring(0, 3);
                        const attachmentBlob = new Blob([response.body], { type: fileExtension });
                        const fileInfo = this._attachmentService.getMimeTypeByFileExtension(fileExtension);

                        if (fileInfo['previewSupported']) {
                            const popUpData = {
                                attachmentInfo: {
                                    attachmentID: attachmentId,
                                    fileName: fileName,
                                    fileExtension: fileExtension,
                                },
                            };

                            let attchmentGroupName = this._popUpService.StaticAliases.Attachment;

                            // ctrl-click - open a new window
                            if (params.event['ctrlKey']) {
                                attchmentGroupName = attchmentGroupName + new Date().getTime();
                            }

                            const globalAttachmentsPopUpRef = this._popUpService.getByAliasGroup(attchmentGroupName)[0];
                            if (globalAttachmentsPopUpRef && globalAttachmentsPopUpRef.isOpen()) {
                                globalAttachmentsPopUpRef.publish(
                                    this._popUpService.Topics.Attachment.AttachmentChanged,
                                    popUpData,
                                );
                            } else {
                                const popUpRef = await this._popUpService.openAttachmentPreview(
                                    attchmentGroupName,
                                    globalAttachmentsPopUpRef,
                                );
                                popUpRef.publish(this._popUpService.Topics.Attachment.AttachmentChanged, popUpData);
                            }
                        } else {
                            await this._documentService.saveFile(attachmentBlob, fileName);
                        }

                        params.colDef['pdfLoading'] = undefined;
                        params.api.redrawRows();
                    },
                };
            default:
                return {};
        }
    }

    private _getCommentCell(): ColDef {
        return {
            cellRenderer: (params) => {
                if (params.value != 0) {
                    return '<i class="fa fa-commenting" aria-hidden="true" ></i>';
                }
                return '';
            },
            cellClass: 'text-align-center',
            headerName: '',
            width: 40,
            minWidth: 40,
            maxWidth: 40,
            hide: false,
        };
    }

    private _getNavigationalFieldValueByName(rowData, fieldName) {
        const navigationField = this._smartSearchService.getFieldByDisplayName(fieldName);
        // get value for navigational field
        return rowData[`n${navigationField.advancedSearchFieldID}`];
    }

    private _setPersistentState(
        rowNode: IRowNode,
        source: string,
        gridApi: GridApi,
        gridTracker: AgGridMultiSelectTracker,
    ): void {
        this._advancedSearchPersistenceService.scrollLocationIndex = rowNode ? rowNode.rowIndex : 0;
        this._advancedSearchPersistenceService.setGridState(
            gridApi.getColumnState(),
            gridApi.getFilterModel(),
            gridTracker.getSelectedRowsModel(),
        );
        switch (source) {
            case 'smart':
                this._nubService.showAdvancedSearchNub();
                break;
            case 'av':
                this._nubService.showActionViewNub();
                break;
        }
    }

    private _constructDateIfAbsent(data: any): Date {
        switch (data) {
            case null:
                return new Date('01-01-2200');
            case 'N/A':
                return new Date('01-01-2300');
            default:
                return new Date(data);
        }
    }

    private _fieldSpecificClickEvents(field: AdvancedSearchField) {
        switch (field.advancedSearchFieldID) {
            // Document Intake Number of Comments
            case 9007:
                return (rowData) => {
                    this._commentsService.getEntityDataForTasks([rowData.n10908]).then((data) => {
                        this._commentsModalService.openCommentModal(data[0]);
                    });
                };
            // Assessment DRTT Number of Comments
            case 1052:
                return (rowData) => {
                    console.log('opening DRTT comment modal ');
                    // this field can be displayed in either AV or SMART
                    // if the invoiceID is available use that, otherwise see if the taskID is available (taskID would not be available in SMART)
                    if (rowData.n1000) {
                        // SMART, we have assesmentID
                        this._commentsService.getEntityData(rowData.n1000, EntityTypeIds.ASSESSMENT).then((data) => {
                            console.log('opening comment modal using entity, data:', data);
                            data.commentTypeID = 4;
                            this._commentsModalService.openCommentModal(data);
                        });
                    } else {
                        this._commentsService.getEntityDataForTasks([rowData.n10908]).then((data) => {
                            // n10908 == SubjectTaskID
                            console.log('opening comment modal using task, data:', data);
                            data[0].commentTypeID = 4;
                            this._commentsModalService.openCommentModal(data[0]);
                        });
                    }
                };

            // Bill DRTT Number of Comments
            case 1456:
                return (rowData) => {
                    console.log('opening Bill DRTT comment modal ');
                    // this field can be displayed in either AV or SMART
                    // if the invoiceID is available use that, otherwise see if the taskID is available (taskID would not be available in SMART)
                    if (rowData.n1498) {
                        // SMART, we have bill ID
                        this._commentsService.getEntityData(rowData.n1498, EntityTypeIds.TAX_BILL).then((data) => {
                            console.log('opening comment modal using entity, data:', data);
                            data.commentTypeID = 4;
                            this._commentsModalService.openCommentModal(data);
                        });
                    } else {
                        this._commentsService.getEntityDataForTasks([rowData.n10908]).then((data) => {
                            // n10908 == SubjectTaskID
                            console.log('opening comment modal using task, data:', data);
                            data[0].commentTypeID = 4;
                            this._commentsModalService.openCommentModal(data[0]);
                        });
                    }
                };

            // Invoice Number of Comments
            case 1916:
                return (rowData) => {
                    // this field can be displayed in either AV or SMART
                    // if the invoiceID is available use that, otherwise see if the taskID is available (taskID would not be available in SMART)
                    if (rowData.n1900) {
                        this._commentsService.getEntityData(rowData.n1900, EntityTypeIds.INVOICE).then((data) => {
                            this._commentsModalService.openCommentModal(data);
                        });
                    } else {
                        this._commentsService.getEntityDataForTasks([rowData.n10908]).then((data) => {
                            this._commentsModalService.openCommentModal(data[0]);
                        });
                    }
                };

            case 1444: // TAX_BILL Number of Comments
                return (rowData) => {
                    // this field can be displayed in either AV or SMART
                    // if the BILLID is available use that, otherwise see if the taskID is available (taskID would not be available in SMART)
                    if (rowData.n1498) {
                        this._commentsService.getEntityData(rowData.n1498, EntityTypeIds.TAX_BILL).then((data) => {
                            this._commentsModalService.openCommentModal(data);
                        });
                    } else {
                        // use task
                        this._commentsService.getEntityDataForTasks([rowData.n10908]).then((data) => {
                            this._commentsModalService.openCommentModal(data[0]);
                        });
                    }
                };

            case 1044: // Revision Number of Comments
                return (rowData) => {
                    // this field can be displayed in either AV or SMART
                    // if the RevisionID is available use that, otherwise see if the taskID is available (taskID would not be available in SMART)
                    if (rowData.n1000) {
                        this._commentsService.getEntityData(rowData.n1000, EntityTypeIds.ASSESSMENT).then((data) => {
                            this._commentsModalService.openCommentModal(data);
                        });
                    } else {
                        // use task
                        this._commentsService.getEntityDataForTasks([rowData.n10908]).then((data) => {
                            this._commentsModalService.openCommentModal(data[0]);
                        });
                    }
                };
        }
    }
}
